import React, { useState, useEffect } from 'react';
import { useUser, SignInButton } from '@clerk/clerk-react';

const BACKGROUND_IMAGES = [
  {
    url: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?q=80&w=2144&auto=format&fit=crop',
    label: 'METROPOLIS'
  },
  {
    url: 'https://images.unsplash.com/photo-1621416848440-2369c44ae7b7?q=80&w=2070&auto=format&fit=crop',
    label: 'CRYPTO'
  },
  {
    url: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=2070&auto=format&fit=crop',
    label: 'INMUEBLES'
  },
  {
    url: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2073&auto=format&fit=crop',
    label: 'RWA'
  },
  {
    url: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop',
    label: 'INSTITUTIONAL'
  }
];

const Hero: React.FC = () => {
  const { user, isLoaded } = useUser();
  const [bgIndex, setBgIndex] = useState(0);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loadedIndices, setLoadedIndices] = useState<Set<number>>(new Set([0]));
  const [failedIndices, setFailedIndices] = useState<Set<number>>(new Set());

  useEffect(() => {
    const interval = setInterval(() => {
      setBgIndex((prev) => {
        let nextIndex = (prev + 1) % BACKGROUND_IMAGES.length;
        // Si la siguiente imagen falló o no está cargada, buscar una que sí funcione
        while (failedIndices.has(nextIndex) && loadedIndices.size < BACKGROUND_IMAGES.length) {
          nextIndex = (nextIndex + 1) % BACKGROUND_IMAGES.length;
        }
        return nextIndex;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [failedIndices, loadedIndices]);

  const handleImageLoad = (index: number) => {
    setLoadedIndices((prev) => new Set([...prev, index]));
  };

  const handleImageError = (index: number) => {
    setFailedIndices((prev) => new Set([...prev, index]));
    setLoadedIndices((prev) => {
      const updated = new Set(prev);
      updated.delete(index);
      return updated;
    });
  };

  return (
    <div className="relative">
      {/* Background Image Slider */}
      <div className="absolute top-0 left-0 w-full h-[95vh] overflow-hidden z-0">
        {BACKGROUND_IMAGES.map((img, idx) => (
          <div
            key={img.url}
            className={`absolute inset-0 transition-opacity duration-1500 ease-in-out ${
              idx === bgIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={img.url}
              alt={img.label}
              onLoad={() => handleImageLoad(idx)}
              onError={() => handleImageError(idx)}
              className="w-full h-full object-cover brightness-[0.35] grayscale contrast-[1.1]"
            />
            {/* Visual Label */}
            <div className="absolute bottom-40 left-6 sm:left-12 lg:left-20 opacity-[0.05] select-none pointer-events-none">
              <span className="text-5xl sm:text-8xl lg:text-9xl font-black tracking-tighter text-white uppercase">{img.label}</span>
            </div>
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
      </div>

      <div className="relative z-10 flex flex-col min-h-screen">
        <div className="flex-1 flex flex-col justify-center">
          <section className="relative w-full flex flex-col items-center justify-center text-center px-6 py-10 sm:py-20 lg:py-0">
            <div className="max-w-5xl z-10 animate-fade-in px-2 sm:px-0">
              <h1 className="text-4xl sm:text-6xl lg:text-8xl font-bold tracking-tight text-white mb-6 sm:mb-8 font-display leading-[1.05]">
                Welcome to
                <br className="hidden sm:block" />
                NeoPawn Economy
              </h1>
              
              <p className="text-sm sm:text-lg lg:text-xl text-white/80 max-w-lg lg:max-w-2xl mx-auto mb-10 sm:mb-14 leading-relaxed font-light px-2 sm:px-4">
                Institutional-grade platforms, assets, and infrastructure designed to bring financial markets onchain through the Reflecter Labs framework.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
                <SignInButton mode="modal">
                  <button className="w-full sm:w-auto bg-white text-black px-10 py-3.5 font-bold transition-all text-[13px] rounded-[4px] min-w-[180px] hover:bg-white/90 active:scale-95">
                    Learn More
                  </button>
                </SignInButton>
                <button className="w-full sm:w-auto text-white font-semibold hover:text-white/70 transition-colors text-[13px] py-3.5 tracking-wide">
                  Read our Manifesto
                </button>
              </div>
            </div>
          </section>
        </div>
        
        {/* Partner Logos */}
        <div className="w-full py-8 sm:py-12 px-4 sm:px-12 lg:px-20 flex flex-wrap items-center justify-center gap-x-6 sm:gap-x-12 gap-y-6 sm:gap-y-8 opacity-60 transition-opacity hover:opacity-100">
          {['BLACKROCK', 'SEI', 'ETHEREUM', 'GOLDMAN SACHS', 'SOLANA', 'SECURITIZE', 'AON', 'JUPITER', 'APTOS'].map((partner, index) => (
            <div key={index} className="flex items-center justify-center">
              <span className="text-sm sm:text-lg font-bold text-white tracking-tighter uppercase font-display whitespace-nowrap">
                {partner}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Terms and Conditions Modal - Bottom Right */}
      {!acceptedTerms && (
        <div className="fixed bottom-6 right-6 sm:bottom-12 sm:right-12 z-[100] max-w-[calc(100vw-3rem)] sm:max-w-xs p-8 bg-black border border-white/10 rounded-sm shadow-2xl animate-fade-in">
          <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-white mb-4">Términos y condiciones</h4>
          <p className="text-[11px] leading-relaxed text-white/40 mb-8 font-medium">
            Al acceder a la plataforma NeoPawn, aceptas nuestros términos de servicio y política de privacidad.
          </p>
          <button
            onClick={() => setAcceptedTerms(true)}
            className="w-full bg-white text-black text-[11px] font-black uppercase tracking-widest py-4 transition-all active:scale-95"
          >
            Aceptar
          </button>
        </div>
      )}
    </div>
  );
};

export default Hero;
